import { useEffect } from 'react';
import { AnyAction } from 'redux';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
  selectIsProductRecommendationsFeatureFlagOn,
  selectIsUserContextLoaded,
} from 'core/userContext/selectors';
import { fetchFieldMappingsRequest } from 'modules/catalog/collectionBuilder/actions';
import {
  addSearchGroup,
  changeCombinator,
  initializeQuery,
  moveSearchQuery,
  reorderSearchQuery,
  resetQueryBuilder,
} from '../actions';
import {
  selectCloningValidationMessages,
  selectCombinator,
  selectIsViewMode,
  selectSearchGroups,
} from '../selectors';
import QueryBuilder from './QueryBuilder';
import { SearchQueryIndex, QueryBuilderV1Props } from '../types';

type StateProps =
  | 'combinator'
  | 'isProductRecommendationsOn'
  | 'isUserContextLoaded'
  | 'isViewMode'
  | 'searchGroups'
  | 'validationMessages'
  | 'cloningValidationMessages';

type DispatchProps =
  | 'initializeQuery'
  | 'fetchUserMappings'
  | 'onAddSearchGroup'
  | 'onChangeCombinator'
  | 'onSearchQueryMove'
  | 'onSearchQueryReorder';

export type Props = Omit<QueryBuilderV1Props, StateProps | DispatchProps | 'store'>;

const useQbState = () => {
  const combinator: QueryBuilderV1Props['combinator'] = useSelector(selectCombinator);
  const isProductRecommendationsOn: QueryBuilderV1Props['isProductRecommendationsOn'] = useSelector(
    selectIsProductRecommendationsFeatureFlagOn,
  );
  const isUserContextLoaded: QueryBuilderV1Props['isUserContextLoaded'] =
    useSelector(selectIsUserContextLoaded);
  const isViewMode: QueryBuilderV1Props['isViewMode'] = useSelector(selectIsViewMode);
  const searchGroups: QueryBuilderV1Props['searchGroups'] = useSelector(selectSearchGroups);
  const cloningValidationMessages: QueryBuilderV1Props['cloningValidationMessages'] = useSelector(
    selectCloningValidationMessages,
  );

  return {
    combinator,
    isProductRecommendationsOn,
    isUserContextLoaded,
    isViewMode,
    searchGroups,
    cloningValidationMessages,
  };
};

const useQbActions = () => {
  const dispatch = useDispatch();
  const initialize: QueryBuilderV1Props['initializeQuery'] = (
    searchGroup: Record<string, unknown>,
    qbProps: Record<string, unknown>,
  ) => dispatch(initializeQuery(searchGroup, qbProps) as AnyAction);
  const fetchUserMappings: QueryBuilderV1Props['fetchUserMappings'] = () =>
    dispatch(fetchFieldMappingsRequest());
  const onAddSearchGroup: QueryBuilderV1Props['onAddSearchGroup'] = (
    searchGroup: Record<string, unknown>,
  ) => dispatch(addSearchGroup(searchGroup) as AnyAction);
  const onChangeCombinator: QueryBuilderV1Props['onChangeCombinator'] = (combinator: string) =>
    dispatch(changeCombinator(combinator) as AnyAction);
  const onSearchQueryMove: QueryBuilderV1Props['onSearchQueryMove'] = (
    searchQueryIndex: SearchQueryIndex,
    searchGroupId: string,
    newSearchQueryIndex: SearchQueryIndex,
    newSearchGroupId: string,
  ) =>
    dispatch(
      moveSearchQuery(
        searchQueryIndex,
        searchGroupId,
        newSearchQueryIndex,
        newSearchGroupId,
      ) as AnyAction,
    );
  const onSearchQueryReorder: QueryBuilderV1Props['onSearchQueryReorder'] = (
    searchQueryIndex: SearchQueryIndex,
    newSearchQueryIndex: SearchQueryIndex,
    searchGroupId: string,
  ) =>
    dispatch(reorderSearchQuery(searchQueryIndex, newSearchQueryIndex, searchGroupId) as AnyAction);
  const reset = () => dispatch(resetQueryBuilder());

  return {
    initializeQuery: initialize,
    fetchUserMappings,
    onAddSearchGroup,
    onChangeCombinator,
    onSearchQueryMove,
    onSearchQueryReorder,
    resetQueryBuilder: reset,
  };
};

export const QueryBuilderV2 = (passedProps: Props) => {
  const store = useStore();
  const qbState = useQbState();
  const { resetQueryBuilder: reset, ...qbActions } = useQbActions();

  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  return <QueryBuilder {...passedProps} {...qbActions} {...qbState} store={store} />;
};
